import PaginationButtons from "@/components/PaginationButtons";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/v2/types/Datatable";
import parseUrlToQueryParams from "@/v2/utils/parseUrlToQueryParams";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LoadingAnimation from "../../ui/LoadingAnimation";

const Loader = () => {
    return (
        <div className="h-4 w-4">
            <LoadingAnimation />
        </div>
    );
};

export const TablePagination = ({
    pagination,
}: {
    pagination?: Pagination;
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParams = parseUrlToQueryParams(searchParams);

    const totalCount = pagination?.total_data_count;
    const hasNext = pagination?.has_next;
    const hasPrev = pagination?.has_prev;

    const [limit, setLimit] = useState(() => queryParams?.limit ?? 20);
    const [skip, setSkip] = useState(() => queryParams?.skip ?? 0);

    // Sync state with URL params when they change externally
    useEffect(() => {
        const urlLimit = queryParams?.limit ?? 20;
        const urlSkip = queryParams?.skip ?? 0;
        if (urlLimit !== limit) setLimit(urlLimit);
        if (urlSkip !== skip) setSkip(urlSkip);
    }, [queryParams?.limit, queryParams?.skip]);

    const isLoading = totalCount === undefined || totalCount === null;

    const handlePreviousPage = () => {
        const newSkip = Math.max(0, skip - limit);
        setSkip(newSkip);
        const params = new URLSearchParams(searchParams);
        params.set("skip", newSkip.toString());
        setSearchParams(params);
    };

    const handleNextPage = () => {
        const newSkip = skip + limit;
        setSkip(newSkip);
        const params = new URLSearchParams(searchParams);
        params.set("skip", newSkip.toString());
        setSearchParams(params);
    };

    const handleLimitChange = (value: number) => {
        setSkip(0);
        setLimit(value);
        const params = new URLSearchParams(searchParams);
        params.set("skip", "0");
        params.set("limit", value.toString());
        setSearchParams(params);
    };

    const currentPage = Math.floor(skip / limit) + 1;
    const totalPages = totalCount ? Math.ceil(totalCount / limit) : 0;
    const startRow = totalCount === 0 ? 0 : skip + 1;
    const endRow = totalCount && Math.min(skip + limit, totalCount);

    return (
        <div className="flex flex-col gap-4 border-t bg-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Rows per page section */}
            <div className="flex items-center gap-3">
                <Label className="whitespace-nowrap text-sm font-medium text-muted-foreground">
                    Rows per page:
                </Label>
                <Select
                    value={String(limit)}
                    onValueChange={(value) => handleLimitChange(Number(value))}
                >
                    <SelectTrigger className="h-9 w-[75px] border-border transition-colors hover:bg-accent">
                        <SelectValue placeholder={limit} />
                    </SelectTrigger>
                    <SelectContent>
                        {[10, 20, 30, 40, 50, 75, 100].map((pageSize) => (
                            <SelectItem
                                value={String(pageSize)}
                                key={pageSize}
                                className="cursor-pointer"
                            >
                                {pageSize}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="flex gap-2 whitespace-nowrap text-sm text-muted-foreground">
                    <span>{startRow}</span> <span>-</span>
                    <span>{isLoading ? <Loader /> : endRow}</span>
                    <span>of</span>
                    <span>{totalCount?.toLocaleString() || <Loader />}</span>
                </span>
            </div>

            {/* Pagination controls */}
            <div className="flex items-center gap-2">
                <Button
                    disabled={!hasPrev}
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    className="gap-1 font-medium transition-all hover:bg-accent disabled:opacity-50"
                >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Previous</span>
                </Button>

                <PaginationButtons
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        const newSkip = (page - 1) * limit;
                        setSkip(newSkip);
                        const params = new URLSearchParams(searchParams);
                        params.set("skip", String(newSkip));
                        params.set("limit", String(limit));
                        setSearchParams(params);
                    }}
                />

                <Button
                    disabled={!hasNext}
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    className="gap-1 font-medium transition-all hover:bg-accent disabled:opacity-50"
                >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
};
